import React from 'react';
import Box from '@mui/material/Box';
import * as Sentry from '@sentry/react';
import { palette } from '../../themes/theme';

interface ErrorBoundaryProps {
  children: React.ReactNode;
}

interface ErrorBoundaryState {
  hasError: boolean;
  error: Error | null;
  errorInfo: React.ErrorInfo | null;
}

class ErrorBoundary extends React.Component<ErrorBoundaryProps, ErrorBoundaryState> {
  constructor(props: ErrorBoundaryProps) {
    super(props);
    this.state = { 
      hasError: false,
      error: null,
      errorInfo: null
    };
  }

  static getDerivedStateFromError(error: Error): Partial<ErrorBoundaryState> {
    return { hasError: true, error };
  }

  componentDidCatch(error: Error, errorInfo: React.ErrorInfo) {
    this.setState({ error, errorInfo });
    Sentry.captureException(error, {
      extra: { componentStack: errorInfo.componentStack },
    });
  }

  render() {
    if (this.state.hasError) {
      return (
        <Box sx={{
          padding: '20px',
          margin: '20px',
          border: `1px solid ${palette.status.errorBorder}`,
          borderRadius: '4px',
          backgroundColor: palette.status.errorLight,
          color: palette.status.errorDark
        }}>
          <h2 style={{ marginTop: 0 }}>Something went wrong</h2>
          <p>We're sorry - something went wrong on our end.</p>
          <Box component="details" sx={{ 
            whiteSpace: 'pre-wrap',
            marginTop: '10px',
            padding: '10px',
            backgroundColor: 'rgba(0,0,0,0.05)',
            borderRadius: '4px'
          }}>
            <summary>Error Details</summary>
            {this.state.error && this.state.error.toString()}
            <br />
            {this.state.errorInfo && this.state.errorInfo.componentStack}
          </Box>
          <Box
            component="button"
            onClick={() => window.location.reload()}
            sx={{
              marginTop: '15px',
              padding: '8px 16px',
              backgroundColor: palette.status.errorDark,
              color: 'white',
              border: 'none',
              borderRadius: '4px',
              cursor: 'pointer'
            }}
          >
            Reload Page
          </Box>
        </Box>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
